import React, { useState, useEffect, createContext, useMemo } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import Box from '@mui/material/Box';
import ThemeModeToggleFab from './components/monitoring/ThemeModeToggleFab';
import Link from '@mui/material/Link';
import { WebSocketProvider } from './contexts/WebSocketContext';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { OperatorMessageProvider } from './contexts/OperatorMessageContext';
import { FacilityBoardProvider } from './contexts/FacilityBoardContext';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { SnackbarProvider } from 'notistack';
import CircularProgress from '@mui/material/CircularProgress';
import 'antd/dist/reset.css';  // antd 스타일 import
import { EventProvider } from './contexts/EventContext';
import MultiEventHandler from './components/alerts/MultiEventHandler';

// Page/Component Imports
import Login from './pages/Login';
import CentralControl from './pages/CentralControl';
import MainMonitoring from './pages/MainMonitoring';
import PoolManagement from './pages/PoolManagement';
import PoolMonitoring from './pages/PoolMonitoring';
import PoolMonitoringList from './pages/PoolMonitoringList';
import PoolDetail from './pages/PoolDetail';
import NotFound from './pages/NotFound';
import ContactManagement from './pages/contact/ContactManagement';
import PoolSettingsPage from './pages/pool/PoolSettings';
import PoolAccessGuard from './components/auth/PoolAccessGuard';
import AccessKeyGate from './components/auth/AccessKeyGate';
import Register from './pages/Register';
import ResetPassword from './pages/ResetPassword';
import CctvFullscreen from './pages/CctvFullscreen';
import CctvCentralMonitoringPopupPage from './pages/CctvCentralMonitoringPopupPage';
import CctvShortPoolPopupRoute from './components/monitoring/CctvShortPoolPopupRoute';
import PoolShortEntryRoute from './components/pools/PoolShortEntryRoute';
import PoolShortMonitoringRoute from './components/pools/PoolShortMonitoringRoute';
import CctvLegacyPopupRedirect from './components/monitoring/CctvLegacyPopupRedirect';
import { isIndividualPoolSitePath } from './components/monitoring/cctvPopupRoutes';
import { resolveMonitoringGateRedirect } from './utils/monitoringAuthRedirect';
import AdminPasswordVerification from './components/auth/AdminPasswordVerification';
import PoolList from './components/pools/PoolList';
import CentralMonitoring from './pages/CentralMonitoring'; // import 활성화
import IndividualMonitoringPage from './pages/IndividualMonitoringPage';
import PhysiologicalMonitoringPage from './pages/PhysiologicalMonitoringPage'; // 생체 신호 모니터링 페이지 임포트
import OperatorMessagesPage from './pages/OperatorMessagesPage';
import CommunicationPopupPage from './pages/CommunicationPopupPage';
import { theme as lightTheme } from './theme'; // Assuming theme.ts exports 'theme'
import MainLayout from './layouts/MainLayout';
import PoolsPage from './pages/PoolsPage';
import MonitoringPage from './pages/MonitoringPage';
import AlertsPage from './pages/AlertsPage';
import WearableManagementPage from './pages/WearableManagementPage';
import { getPostLoginHomePath, isSuperAdminUser } from './utils/adminAccess';
import BrandMenuPage from './pages/BrandMenuPage';
import { MenuPolicyProvider, useMenuPolicy } from './brand/MenuPolicyContext';
import { isAdminPathDenied, nextAllowedAdminPath } from './brand/menuCatalog';

const HomeRedirect: React.FC = () => {
  const { authState } = useAuth();
  return <Navigate to={getPostLoginHomePath(authState.user)} replace />;
};

/** 일반관리자는 개별 관제만 — 관제센터 메뉴 경로 차단 */
const REGULAR_ADMIN_BLOCKED_PREFIXES = [
  '/central-control',
  '/central-monitoring',
  '/dashboard',
  '/pool-management',
  '/pool-monitoring',
  '/settings',
  '/physiological-monitoring',
  '/wearable-management',
  '/contact',
  '/alerts',
  '/pools',
  '/monitoring',
  '/brand-menu',
];

function isRegularAdminBlockedPath(pathname: string): boolean {
  return REGULAR_ADMIN_BLOCKED_PREFIXES.some(
    (p) => pathname === p || pathname.startsWith(`${p}/`)
  );
}

// ColorModeContext 생성
export const ColorModeContext = createContext({
  toggleColorMode: () => {},
  mode: 'light',
});

// Loading Component
const LoadingScreen: React.FC = () => (
  <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
    <CircularProgress />
  </Box>
);

// Main Application Routes Component
const MainAppRoutes: React.FC = () => {
  console.log('[MainAppRoutes] Rendering.');
  const location = useLocation();
  const { authState } = useAuth();
  const { grants } = useMenuPolicy();

  // Safeguard check
  if (['/login', '/register', '/reset-password', '/verify-admin'].includes(location.pathname)) {
    console.log(`[MainAppRoutes] Authenticated user unexpectedly on restricted path ${location.pathname}. Redirecting home (safeguard).`);
    return <HomeRedirect />;
  }

  // 관리자 로그인 상태 확인
  const isAdminLoggedIn = authState.isAdminPasswordVerified && authState.isAuthenticated;

  // 현재 경로가 pool_code를 포함하는 개별 시설 접근 경로인지 확인
  const isIndividualPoolPath =
    isIndividualPoolSitePath(location.pathname);

  const gateRedirect = resolveMonitoringGateRedirect(authState, {
    isIndividualPoolPath,
  });
  if (gateRedirect) {
    console.log('[MainAppRoutes] Access denied, redirecting to', gateRedirect);
    return <Navigate to={gateRedirect} replace />;
  }

  // 일반관리자: 개별 관제 외 관제시스템 메뉴 경로 차단
  if (
    authState.isAuthenticated &&
    !authState.loading &&
    !isSuperAdminUser(authState.user) &&
    isRegularAdminBlockedPath(location.pathname)
  ) {
    return <Navigate to="/individual-monitoring" replace />;
  }

  if (
    authState.isAuthenticated &&
    !authState.loading &&
    !authState.user?.is_brand_manager
  ) {
    const menuGrade = isSuperAdminUser(authState.user) ? 'super' : 'manager';
    const granted = (id: string) => {
      const row = grants[id];
      if (!row) return true;
      return row[menuGrade] !== false;
    };
    if (isAdminPathDenied(location.pathname, location.search, menuGrade, granted)) {
      const next = nextAllowedAdminPath(location.pathname, location.search, granted);
      if (next) return <Navigate to={next} replace />;
    }
  }

  return (
    <Routes>
      {/* Root path redirect - 로그인 성공 시 기본 경로 */}
      <Route path="/" element={<HomeRedirect />} />
      <Route path="/dashboard" element={<HomeRedirect />} />
      <Route path="/central-control" element={<CentralControl />} />
      <Route path="/main-monitoring" element={<MainMonitoring />} />
      <Route path="/central-monitoring" element={<CentralMonitoring />} />
      <Route path="/individual-monitoring" element={<IndividualMonitoringPage />} />
      <Route path="/brand-menu" element={<BrandMenuPage />} />
      <Route path="/communication" element={<CommunicationPopupPage />} />
      <Route path="/operator-messages" element={<OperatorMessagesPage />} />
      <Route path="/physiological-monitoring" element={<PhysiologicalMonitoringPage />} />
      <Route path="/pool-management" element={<PoolList />} />
      
      {/* 개별 시설 접근 - PoolAccessGuard로 보호 */}
      <Route path="/pool-management/:poolCode" element={
        <PoolAccessGuard><PoolManagement /></PoolAccessGuard>
      } />
      
      <Route path="/pool-monitoring" element={<PoolMonitoringList />} />
      
      {/* 개별 시설 모니터링 접근 - PoolAccessGuard로 보호 */}
      <Route path="/pool-monitoring/:poolCode" element={
        <PoolAccessGuard><PoolMonitoring /></PoolAccessGuard>
      } />
      
      {/* 나머지 시설 관련 경로 */}
      <Route path="/pool/:code" element={<PoolDetail />} />
      <Route path="/pool/id/:poolId" element={<PoolDetail />} />
      <Route path="/pool/:poolCode/settings" element={<PoolSettingsPage />} />
      <Route path="/settings" element={<Navigate to="/central-control?tab=system" replace />} />
      
      <Route path="/contact-management" element={<ContactManagement />} />
      <Route path="/cctv-fullscreen" element={<CctvFullscreen />} />
      <Route path="/all/cctv" element={<CctvCentralMonitoringPopupPage />} />
      <Route
        path="/cctv-popup/pool/:poolCode/:mode"
        element={<CctvLegacyPopupRedirect />}
      />
      <Route path="/cctv-popup/central/all" element={<Navigate to="/all/cctv" replace />} />
      <Route path="/:poolCode/monitoring" element={<PoolShortMonitoringRoute />} />
      <Route path="/:poolCode/:mode" element={<CctvShortPoolPopupRoute />} />
      <Route path="/:poolCode" element={<PoolShortEntryRoute />} />
      <Route path="*" element={<NotFound />} />
    </Routes>
  );
};

// AppContent: Handles routing based on auth state
const AppContent: React.FC = () => {
  const { authState } = useAuth();
  const location = useLocation();

  console.log(`[AppContent] Rendering. Path: ${location.pathname}. State:`, {
    loading: authState.loading,
    isAdminVerified: authState.isAdminPasswordVerified,
    isAuth: authState.isAuthenticated,
  });

  if (authState.loading) {
    console.log('[AppContent] Showing loading screen.');
    return <LoadingScreen />;
  }

  // 관리자 로그인 상태 확인
  const isAdminLoggedIn = authState.isAdminPasswordVerified && authState.isAuthenticated;

  // 현재 경로가 pool_code를 포함하는 개별 시설 접근 경로인지 확인
  const isIndividualPoolPath = isIndividualPoolSitePath(location.pathname);

  const gateRedirect = resolveMonitoringGateRedirect(authState, {
    isIndividualPoolPath,
  });

  // 공개 경로인지 확인
  const isPublicPath = ['/login', '/register', '/reset-password', '/verify-admin'].includes(location.pathname);

  return (
    <>
      {/* Skip link (keep as is) */}
      <Link href="#main-content" sx={{ position: 'absolute', left: '-999px', width: '1px', height: '1px' }}>
        Skip to main content
      </Link>
      <Routes>
        {/* 1. 관제센터 비밀번호 인증 확인 */}
        <Route path="/verify-admin" element={
          isAdminLoggedIn
            ? <HomeRedirect />
            : <AdminPasswordVerification />
        } />

        {/* 2. 인증 경로 (관리자 로그인/등록) */}
        <Route path="/login" element={
          authState.isAdminPasswordVerified
            ? isAdminLoggedIn
              ? <HomeRedirect />
              : <Login />
            : <Navigate to="/verify-admin" replace />
        } />

        <Route path="/register" element={
          authState.isAdminPasswordVerified
            ? isAdminLoggedIn
              ? <HomeRedirect />
              : <Register />
            : <Navigate to="/verify-admin" replace />
        } />

        <Route path="/reset-password" element={
          authState.isAdminPasswordVerified
            ? isAdminLoggedIn
              ? <HomeRedirect />
              : <ResetPassword />
            : <Navigate to="/verify-admin" replace />
        } />

        {/* 3. 루트 경로 */}
        <Route path="/" element={
          !authState.isAdminPasswordVerified
            ? <Navigate to="/verify-admin" replace />
            : !authState.isAuthenticated
              ? <Navigate to="/login" replace />
              : <HomeRedirect />
        } />

        {/* 4. 보호된 경로 */}
        <Route path="/*" element={ 
          gateRedirect
            ? <Navigate to={gateRedirect} replace />
            : <MainAppRoutes />
        } />
      </Routes>
    </>
  );
};

// App Component (structure mostly the same, wrapping with providers and Router)
function App() {
  console.log('[App] Rendering');
  const storedMode = localStorage.getItem('themeMode') || 'light';
  const [mode, setMode] = useState<'light' | 'dark'>(storedMode as 'light' | 'dark');
  const colorMode = useMemo(
    () => ({
      toggleColorMode: () => { setMode((prev) => (prev === 'light' ? 'dark' : 'light')); },
      mode,
    }),
    [mode],
  );
  useEffect(() => { localStorage.setItem('themeMode', mode); }, [mode]);

  const darkTheme = useMemo(() => createTheme({
    palette: {
      mode: 'dark',
      primary: {
        main: '#00B4D8',
        light: '#48CAE4',
        dark: '#0077B6',
        contrastText: '#ffffff',
      },
      secondary: {
        main: '#023E8A',
        light: '#0077B6',
        dark: '#03045E',
        contrastText: '#ffffff',
      },
      background: {
        default: '#0F172A',
        paper: '#1E293B',
      },
      text: {
        primary: '#E2E8F0',
        secondary: '#CBD5E1',
        disabled: '#64748B',
      },
    },
  }), []); 

  const theme = useMemo(() => (mode === 'light' ? lightTheme : darkTheme), [mode, darkTheme]);

  return (
    <Router>
      <ColorModeContext.Provider value={colorMode}>
        <ThemeProvider theme={theme}>
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <CssBaseline />
            <SnackbarProvider>
              <AuthProvider>
                <MenuPolicyProvider>
                <EventProvider>
                  <WebSocketProvider>
                    <OperatorMessageProvider>
                      <FacilityBoardProvider>
                      <AccessKeyGate>
                        <AppContent />
                      </AccessKeyGate>
                      <ThemeModeToggleFab />
                      </FacilityBoardProvider>
                    </OperatorMessageProvider>
                  </WebSocketProvider>
                </EventProvider>
                </MenuPolicyProvider>
              </AuthProvider>
            </SnackbarProvider>
          </LocalizationProvider>
        </ThemeProvider>
      </ColorModeContext.Provider>
    </Router>
  );
}

export default App;
